import {
  Camera, Search, ShieldCheck, Star, FileText, Mail,
  ClipboardList, Calendar, MapPin, Smartphone, AlertTriangle, BarChart3,
  Video, MessageSquare, PieChart, UserCheck, Globe, ArrowRightLeft,
  Radar, Building2, Target, TrendingUp, Zap, Users,
  Sparkles, SlidersHorizontal, Share2, LayoutGrid, LineChart,
  ArrowRight, ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AnimatedSection, StaggerContainer, StaggerItem } from "./AnimatedSection";

const iconMap = {
  Camera, Search, ShieldCheck, Star, FileText, Mail,
  ClipboardList, Calendar, MapPin, Smartphone, AlertTriangle, BarChart3,
  Video, MessageSquare, PieChart, UserCheck, Globe, ArrowRightLeft,
  Radar, Building2, Target, TrendingUp, Zap, Users,
  Sparkles, SlidersHorizontal, Share2, LayoutGrid, LineChart,
};

function HeroSection({ product }) {
  return (
    <section className="relative overflow-hidden" data-testid={`hero-${product.id}`}>
      <div className="absolute inset-0 dot-grid opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimatedSection>
            <Badge
              variant="outline"
              className={`${product.accent.badge} mb-6 px-4 py-1.5 text-xs tracking-[0.15em] uppercase font-semibold`}
              data-testid={`badge-${product.id}`}
            >
              {product.badge}
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-foreground leading-[1.05] mb-6">
              {product.title}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {product.subtitle}
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="relative">
            <div className={`absolute -inset-4 bg-gradient-to-br ${product.accent.gradient} rounded-3xl opacity-10 blur-2xl gentle-pulse`} />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/10 ring-1 ring-black/5">
              <img
                src={product.heroImage}
                alt={product.title}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function MetricsBar({ product }) {
  return (
    <section className={`${product.accent.lightBg} border-y border-border/40`} data-testid={`metrics-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-12 lg:py-16">
        <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {product.metrics.map((metric, i) => (
            <StaggerItem key={i}>
              <div className="text-center lg:text-left" data-testid={`metric-item-${product.id}-${i}`}>
                <div className={`metric-number text-3xl sm:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r ${product.accent.gradient} bg-clip-text text-transparent`}>
                  {metric.value}
                </div>
                <div className="font-heading font-semibold text-foreground mt-2 text-sm sm:text-base">
                  {metric.label}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {metric.prev}
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

function FeaturesGrid({ product }) {
  return (
    <section className="py-24 lg:py-32" data-testid={`features-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16 lg:mb-20">
          <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${product.accent.iconText}`}>
            Key Capabilities
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-3">
            What I'd Build
          </h2>
        </AnimatedSection>
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {product.features.map((feature, i) => {
            const Icon = iconMap[feature.icon];
            return (
              <StaggerItem key={i}>
                <div
                  className="bg-white border border-border/40 rounded-xl p-8 card-hover group"
                  data-testid={`feature-card-${product.id}-${i}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${product.accent.iconBg} flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110`}>
                    {Icon && <Icon className={`w-6 h-6 ${product.accent.iconText}`} />}
                  </div>
                  <h3 className="font-heading font-bold text-foreground text-lg mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
}

function HowItWorks({ product }) {
  return (
    <section className="py-24 lg:py-32 bg-secondary/50" data-testid={`how-it-works-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16 lg:mb-20">
          <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${product.accent.iconText}`}>
            Process
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-3">
            How It Works
          </h2>
        </AnimatedSection>
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {product.howItWorks.map((step, i) => (
            <StaggerItem key={i}>
              <div className="relative" data-testid={`step-${product.id}-${i}`}>
                {i < product.howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[calc(50%+32px)] w-[calc(100%-64px)] h-px bg-border" />
                )}
                <div className="flex flex-col items-center text-center">
                  <div className={`w-14 h-14 rounded-2xl ${product.accent.stepNum} flex items-center justify-center font-heading font-bold text-xl mb-6 shadow-lg relative z-10`}>
                    {step.step}
                  </div>
                  <h3 className="font-heading font-bold text-foreground text-lg mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                    {step.description}
                  </p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

function ArchitectureSection({ product }) {
  return (
    <section className="py-24 lg:py-32" data-testid={`architecture-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16 lg:mb-20">
          <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${product.accent.iconText}`}>
            Architecture
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-3">
            {product.architecture.title}
          </h2>
          <p className="text-base text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
            {product.architecture.description}
          </p>
        </AnimatedSection>
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {product.architecture.layers.map((layer, i) => (
            <StaggerItem key={i}>
              <div className="relative" data-testid={`arch-layer-${product.id}-${i}`}>
                <div className="bg-white border border-border/40 rounded-xl overflow-hidden card-hover">
                  <div className={`${layer.color} px-6 py-4`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white font-heading font-bold text-sm">
                        {i + 1}
                      </div>
                      <h3 className="font-heading font-bold text-white text-base">
                        {layer.name}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3">
                      {layer.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-3 text-sm text-foreground">
                          <ChevronRight className={`w-4 h-4 ${product.accent.iconText} flex-shrink-0`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {i < product.architecture.layers.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className={`w-6 h-6 ${product.accent.iconText}`} />
                  </div>
                )}
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

function WorkflowSection({ product }) {
  if (!product.workflow) return null;
  return (
    <section className="py-24 lg:py-32 bg-secondary/30" data-testid={`workflow-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16 lg:mb-20">
          <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${product.accent.iconText}`}>
            Product Workflow
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-3">
            What Users Actually See
          </h2>
          <p className="text-base text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
            Step-by-step walkthrough of the core product screens and user experience
          </p>
        </AnimatedSection>
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {product.workflow.map((screen, i) => (
            <StaggerItem key={i}>
              <div data-testid={`workflow-screen-${product.id}-${i}`}>
                <div className="rounded-xl border border-border/60 overflow-hidden bg-white shadow-sm card-hover">
                  <div className="px-4 py-2.5 bg-gray-50/80 border-b border-border/40 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                    </div>
                    <span className="text-xs text-muted-foreground font-medium ml-2">{screen.screen}</span>
                  </div>
                  <div className="p-5 space-y-3">
                    {screen.elements.map((el, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${product.accent.iconBg} ring-2 ${product.accent.ring}`} />
                        <span className="text-xs text-foreground font-medium">{el}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 px-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold ${product.accent.iconText}`}>Step {i + 1}</span>
                    <span className="font-heading font-semibold text-sm text-foreground">{screen.screen}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{screen.description}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

function ApproachSection({ product }) {
  return (
    <section className="py-24 lg:py-32 border-t border-border/40" data-testid={`approach-${product.id}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <AnimatedSection>
          <div className="max-w-3xl">
            <span className={`text-xs tracking-[0.2em] uppercase font-semibold ${product.accent.iconText}`}>
              Build Strategy
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-3 mb-6">
              How I'd Approach This
            </h2>
            <div className={`p-6 rounded-xl border border-border/40 ${product.accent.lightBg}`}>
              <p className="text-base text-foreground leading-relaxed">
                {product.approach}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2" data-testid={`approach-tag-${product.id}-0`}>
                <div className={`w-2 h-2 rounded-full ${product.accent.iconBg}`} />
                <span>Ship early, iterate often</span>
              </div>
              <div className="flex items-center gap-2" data-testid={`approach-tag-${product.id}-1`}>
                <div className={`w-2 h-2 rounded-full ${product.accent.iconBg}`} />
                <span>Event driven architecture</span>
              </div>
              <div className="flex items-center gap-2" data-testid={`approach-tag-${product.id}-2`}>
                <div className={`w-2 h-2 rounded-full ${product.accent.iconBg}`} />
                <span>Iterate with real data</span>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export default function ProductPage({ product }) {
  return (
    <div className="animate-fade-in-up" data-testid={`product-page-${product.id}`}>
      <HeroSection product={product} />
      <FeaturesGrid product={product} />
      <HowItWorks product={product} />
      <ArchitectureSection product={product} />
      <WorkflowSection product={product} />
      <ApproachSection product={product} />
    </div>
  );
}
